import React,{useEffect,useRef,useState}from"react";
import{Autocomplete,Box,Button,Checkbox,FormControlLabel,TextField,Typography}from"@mui/material";
import{useAuth}from"../../../context/AuthContext";
import{useAlert}from"../../../context/AlertContext";

const initialForm={
  equipment:"",
  brand:"",
  equipment_model:"",
  work_area:"",
  equipment_code:"",
  amperage:"",
  low_psi:"",
  high_psi:"",
  voltage:"",
  temperature:"",
  refrigerant_lb:"",
  preventive_maintenance:false,
  corrective_maintenance:false,
  technical_assistance:false,
  installation:false,
  antecedentes:"",
  trabajo_realizado:"",
  materiales:"",
};

export default function NewOrder({onCreated}){
  const{authFetch}=useAuth();
  const{showSuccess,showError,showWarning}=useAlert();
  const[supervisors,setSupervisors]=useState([]);
  const[selectedSupervisor,setSelectedSupervisor]=useState(null);
  const[search,setSearch]=useState("");
  const[form,setForm]=useState(initialForm);
  const[images,setImages]=useState([]);
  const[loadingSupervisors,setLoadingSupervisors]=useState(false);
  const[saving,setSaving]=useState(false);
  const fileInputRef=useRef(null);

  useEffect(()=>{
    const timer=setTimeout(async()=>{
      setLoadingSupervisors(true);
      try{
        const response=await authFetch(`/api/orden/supervisores/?q=${encodeURIComponent(search.trim())}`);
        const data=await response.json();
        if(!response.ok)throw new Error(data.detail||"No se pudieron cargar los supervisores.");
        setSupervisors(Array.isArray(data)?data:Array.isArray(data.supervisors)?data.supervisors:[]);
      }catch(error){
        setSupervisors([]);
        showError(error.message||"No se pudieron cargar los supervisores.");
      }finally{
        setLoadingSupervisors(false);
      }
    },300);
    return()=>clearTimeout(timer);
  },[search,authFetch,showError]);

  const change=e=>{
    const{name,value}=e.target;
    setForm(prev=>({...prev,[name]:value}));
  };

  const changeCheck=e=>{
    const{name,checked}=e.target;
    setForm(prev=>({...prev,[name]:checked}));
  };

  const addImages=e=>{
    const files=Array.from(e.target.files||[]);
    if(files.length)setImages(prev=>[...prev,...files]);
    e.target.value="";
  };

  const removeImage=index=>{
    setImages(prev=>prev.filter((_,i)=>i!==index));
  };

  const submit=async()=>{
    if(!selectedSupervisor?.public_id){
      showWarning("Seleccione un supervisor.");
      return;
    }

    setSaving(true);

    try{
      const body=new FormData();
      body.append("supervisor",selectedSupervisor.public_id);

      Object.entries(form).forEach(([key,value])=>{
        body.append(key,value);
      });

      images.forEach(image=>body.append("images",image));

      const response=await authFetch("/api/orden/",{
        method:"POST",
        body,
      });

      const data=await response.json();

      if(!response.ok){
        if(data.errors)throw new Error(Object.values(data.errors).flat().join(" "));
        throw new Error(data.detail||"No se pudo generar la orden.");
      }

      showSuccess(`Orden ${data.order?.number||""} generada correctamente.`);
      if(onCreated)onCreated(data.order);
    }catch(error){
      showError(error.message||"No se pudo generar la orden.");
    }finally{
      setSaving(false);
    }
  };

  const fieldSx={
    "& .MuiOutlinedInput-root":{borderRadius:2,bgcolor:"#fff"},
  };

  const sectionTitle={
    fontSize:15,
    fontWeight:700,
    color:"#182230",
    mb:1.5,
  };

  return(
    <Box>
      <Typography sx={{fontSize:24,fontWeight:700,color:"#182230",mb:2.5}}>Nueva orden</Typography>

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,p:{xs:2,md:3},maxWidth:1050}}>
        <Typography sx={sectionTitle}>Responsable de revisión</Typography>

        <Autocomplete
          value={selectedSupervisor}
          options={supervisors}
          loading={loadingSupervisors}
          filterOptions={options=>options}
          getOptionLabel={option=>option?.name||""}
          isOptionEqualToValue={(option,value)=>option.public_id===value.public_id}
          onChange={(event,value)=>setSelectedSupervisor(value)}
          onInputChange={(event,value,reason)=>{
            if(reason==="input")setSearch(value);
            if(reason==="clear")setSearch("");
          }}
          noOptionsText="No se encontraron supervisores"
          loadingText="Buscando supervisores..."
          renderOption={(props,option)=>(
            <Box component="li" {...props} key={option.public_id} sx={{display:"flex",flexDirection:"column",alignItems:"flex-start!important",py:"8px!important"}}>
              <Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>
                {option.name}
              </Typography>
              <Typography sx={{fontSize:12,color:"#667085"}}>
                {option.email}{option.phone?` · ${option.phone}`:""}
              </Typography>
            </Box>
          )}
          renderInput={params=>(
            <TextField {...params} label="Supervisor" placeholder="Buscar por nombre, correo o teléfono" size="small" fullWidth sx={fieldSx}/>
          )}
        />

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Datos del equipo</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:2}}>
          <TextField label="Equipo" name="equipment" value={form.equipment} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField label="Marca" name="brand" value={form.brand} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField label="Modelo" name="equipment_model" value={form.equipment_model} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField label="Área de trabajo" name="work_area" value={form.work_area} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField label="Código" name="equipment_code" value={form.equipment_code} onChange={change} size="small" fullWidth sx={fieldSx}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Datos técnicos</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr",md:"1fr 1fr 1fr"},gap:2}}>
          <TextField label="Amperaje" name="amperage" value={form.amperage} onChange={change} placeholder="Ej. 10 A" size="small" fullWidth sx={fieldSx}/>
          <TextField label="Baja PSI" name="low_psi" value={form.low_psi} onChange={change} placeholder="Ej. 60 PSI" size="small" fullWidth sx={fieldSx}/>
          <TextField label="Alta PSI" name="high_psi" value={form.high_psi} onChange={change} placeholder="Ej. 250 PSI" size="small" fullWidth sx={fieldSx}/>
          <TextField label="Voltaje" name="voltage" value={form.voltage} onChange={change} placeholder="Ej. 220 V" size="small" fullWidth sx={fieldSx}/>
          <TextField label="Temperatura" name="temperature" value={form.temperature} onChange={change} placeholder="Ej. 18 °C" size="small" fullWidth sx={fieldSx}/>
          <TextField label="Refrigerante LB" name="refrigerant_lb" value={form.refrigerant_lb} onChange={change} placeholder="Ej. 3.5 LB" size="small" fullWidth sx={fieldSx}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Tipo de servicio</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:.5}}>
          <FormControlLabel control={<Checkbox size="small" name="preventive_maintenance" checked={form.preventive_maintenance} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Mantenimiento preventivo</Typography>}/>
          <FormControlLabel control={<Checkbox size="small" name="corrective_maintenance" checked={form.corrective_maintenance} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Mantenimiento correctivo</Typography>}/>
          <FormControlLabel control={<Checkbox size="small" name="technical_assistance" checked={form.technical_assistance} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Asistencia técnica</Typography>}/>
          <FormControlLabel control={<Checkbox size="small" name="installation" checked={form.installation} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Instalación</Typography>}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Información del trabajo</Typography>

        <Box sx={{display:"grid",gap:2}}>
          <TextField label="Antecedentes" name="antecedentes" value={form.antecedentes} onChange={change} multiline minRows={4} fullWidth sx={fieldSx}/>
          <TextField label="Trabajo realizado" name="trabajo_realizado" value={form.trabajo_realizado} onChange={change} multiline minRows={5} fullWidth sx={fieldSx}/>
          <TextField label="Materiales" name="materiales" value={form.materiales} onChange={change} multiline minRows={4} fullWidth sx={fieldSx}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Evidencia fotográfica</Typography>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
          onChange={addImages}
          style={{display:"none"}}
        />

        <Box sx={{display:"flex",alignItems:"center",gap:1.5,flexWrap:"wrap"}}>
          <Button
            variant="outlined"
            onClick={()=>fileInputRef.current?.click()}
            sx={{borderRadius:2,textTransform:"none",fontWeight:700,color:"#123f6d",borderColor:"#b8c8d8",boxShadow:"none"}}
          >
            Seleccionar imágenes
          </Button>
          <Typography sx={{fontSize:12.5,color:"#667085"}}>JPG, PNG, WEBP, HEIC o HEIF</Typography>
        </Box>

        {images.length>0&&(
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:1,mt:2}}>
            {images.map((image,index)=>(
              <Box key={`${image.name}-${index}`} sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:1,px:1.5,py:1,border:"1px solid #dce3ea",borderRadius:2}}>
                <Box sx={{minWidth:0}}>
                  <Typography sx={{fontSize:12.5,color:"#344054",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{image.name}</Typography>
                  <Typography sx={{fontSize:11,color:"#98A2B3"}}>{(image.size/1024/1024).toFixed(2)} MB</Typography>
                </Box>
                <Button size="small" onClick={()=>removeImage(index)} sx={{minWidth:"auto",color:"#B42318",textTransform:"none",fontSize:12,fontWeight:700}}>
                  Quitar
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{display:"flex",justifyContent:"flex-end",mt:3}}>
          <Button
            variant="contained"
            onClick={submit}
            disabled={saving}
            sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none",px:2.5,"&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}
          >
            {saving?"Generando...":"Generar orden"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}